'use client'

import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import { Loader2, Star } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { useCallback, useMemo, useRef, useState } from 'react'

import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { useUnifiedOrderFormContext } from '@/features/order/order-form/lib/order-form-context'
import { toast } from '@/lib/toast'
import { cn } from '@/lib/utils'
import { formatSymbol } from '@/lib/utils/format-util'
import { useCreatePreset } from '@/service/preset.hook'
import { orderFormCollapsibleConfigAtomFamily } from '@/state/order'
import { PRESET_TOUR_TARGET, presetTourAtom, presetTourDoneAtom } from '@/state/preset-tour'

type OrderFormT = ReturnType<typeof useTranslations<'orderForm'>>

// bps/ticks are unitless shorthand, not user copy — kept out of the message
// catalogue on purpose, same convention as limit-price-field.tsx's unitLabel.
function formatOffset(rawPrice: string, unit: string): string {
  const n = Number(rawPrice)
  if (!rawPrice || Number.isNaN(n) || n === 0) return ''
  const sign = n > 0 ? '+' : ''
  if (unit === 'percent') return `${sign}${rawPrice}%`
  if (unit === 'bps') return `${sign}${rawPrice} bps`
  if (unit === 'ticks') return `${sign}${rawPrice} ticks`
  return `${sign}${rawPrice}`
}

function formatOrderTypeLabel(
  t: OrderFormT,
  isMarketOrder: boolean,
  priceAnchor: string,
  priceOffsetUnit: string,
  price: string
): string {
  if (isMarketOrder) return t('market')
  if (priceAnchor === 'price') return `${t('limit')} @ ${price || '—'}`
  const anchorLabel = t(`priceAnchor.${priceAnchor}` as Parameters<OrderFormT>[0])
  const offset = formatOffset(price, priceOffsetUnit)
  return `${t('limit')} @ ${anchorLabel}${offset ? ` ${offset}` : ''}`
}

function formatFrequency(
  t: OrderFormT,
  mode: string,
  fixed: number,
  min: number,
  max: number
): string {
  const value = mode === 'random' ? `${min}–${max}` : `${fixed}`
  return t('secondsShort', { value })
}

function formatSize(mode: string, fixed: number, min: number, max: number, unit: string): string {
  const value = mode === 'random' ? `${min}–${max}` : `${fixed}`
  return `${value} ${unit}`.trim()
}

interface OrderDraftSummaryProps {
  t: OrderFormT
}

/**
 * Human-readable preview of exactly what buildCurrentPresetPayload is about to
 * save — order type, price, quantity, and account per leg — so the user isn't
 * saving a preset blind. Reads the same context the builder itself reads, not
 * the built PresetPayload, since decoding relative-price enums back out of the
 * wire shape would just be re-deriving what's already sitting in the form.
 */
function OrderDraftSummary({ t }: OrderDraftSummaryProps) {
  const {
    form,
    isMarketOrder,
    isTwapMode,
    isLimitChaseMode,
    getRawValues,
    getSubmissionRows,
    hasExitCondition,
    cardId,
  } = useUnifiedOrderFormContext()
  // Price drafts change on every keystroke. Keep that subscription local to
  // the preset preview instead of routing it through the global form context.
  const collapsibleConfig = useAtomValue(orderFormCollapsibleConfigAtomFamily(cardId))
  const exitConditionPrices = {
    takeProfit: collapsibleConfig?.priceAbove,
    stopLoss: collapsibleConfig?.priceBelow,
  }

  const rows = getSubmissionRows()
  const rawValues = getRawValues()
  const priceAnchor = form.getValues('priceAnchor') ?? 'price'
  const priceOffsetUnit = form.getValues('priceOffsetUnit') ?? 'quote_value'
  const price = form.getValues('price') ?? ''

  const modeLabel = isTwapMode
    ? t('twap')
    : isLimitChaseMode
      ? t('limitChase')
      : formatOrderTypeLabel(t, isMarketOrder, priceAnchor, priceOffsetUnit, price)

  const executionDetail = isTwapMode
    ? `${t('twapExecution')} · ${formatFrequency(
        t,
        rawValues.twapFrequencyMode,
        rawValues.twapFrequencySeconds,
        rawValues.twapFrequencyMinSeconds,
        rawValues.twapFrequencyMaxSeconds
      )} · ${t('minutesShort', { value: rawValues.twapIntervalMinutes })}`
    : null

  return (
    <div className="border-foreground/10 bg-foreground/[0.03] flex flex-col gap-2 border p-3 text-xs">
      <div className="flex items-center justify-between">
        <span className="text-foreground font-semibold">{modeLabel}</span>
        {executionDetail && <span className="text-foreground/50">{executionDetail}</span>}
      </div>
      <div className="flex flex-col gap-1.5">
        {rows.length === 0 ? (
          <span className="text-foreground/50">{t('selectAccountFirst')}</span>
        ) : (
          rows.map((row) => (
            <div
              key={`${row.index}-${row.account.accountId}`}
              className="flex items-center justify-between gap-2"
            >
              <div className="flex min-w-0 items-center gap-1.5">
                <span
                  className={cn(
                    'font-semibold',
                    row.item.side === 'buy' ? 'text-chart-mint' : 'text-chart-red'
                  )}
                >
                  {row.item.side === 'buy' ? t('buy') : t('sell')}
                </span>
                <span className="text-foreground truncate font-medium">
                  {formatSymbol(row.item.symbol)}
                </span>
              </div>
              <div className="text-foreground/50 flex shrink-0 items-center gap-2">
                <span>
                  {row.rawItem.amount} {row.item.amountUnit}
                </span>
                <span className="text-foreground/30">·</span>
                <span>{row.account.name}</span>
              </div>
            </div>
          ))
        )}
      </div>
      {isTwapMode && rows.length > 0 && (
        <div className="text-foreground/50 flex items-center justify-between">
          <span>{t('twapExecutionDescription')}</span>
          <span>
            {formatSize(
              rawValues.twapOrderSizeMode,
              rawValues.twapOrderSize,
              rawValues.twapOrderSizeMin,
              rawValues.twapOrderSizeMax,
              rows[0]?.item.amountUnit ?? ''
            )}{' '}
            {t('perSlice')}
          </span>
        </div>
      )}
      {hasExitCondition && (exitConditionPrices?.takeProfit || exitConditionPrices?.stopLoss) && (
        <div className="border-foreground/10 flex items-center gap-3 border-t pt-1.5">
          {exitConditionPrices?.takeProfit && (
            <span className="text-chart-mint">TP {exitConditionPrices.takeProfit}</span>
          )}
          {exitConditionPrices?.stopLoss && (
            <span className="text-chart-red">SL {exitConditionPrices.stopLoss}</span>
          )}
        </div>
      )}
    </div>
  )
}

export default function SavePresetButton() {
  const t = useTranslations('orderForm')
  const { buildCurrentPresetPayload, presetUnsupportedReason, validateSubmissionRows } =
    useUnifiedOrderFormContext()
  const [open, setOpen] = useState(false)
  const [name, setName] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  const { mutateAsync: createPreset, isPending } = useCreatePreset()
  const [tour, setTour] = useAtom(presetTourAtom)
  const setTourDone = useSetAtom(presetTourDoneAtom)

  // The order form can be in a state presets can't represent at all (Advanced
  // TWAP, Spreader) or would silently misrepresent (an armed entry trigger,
  // which has no field in PresetPayload) — buildCurrentPresetPayload returns
  // null for all of these. Rather than let the user save and find out later,
  // disable the star up front and say why.
  const unsupportedMessage = useMemo(
    () =>
      presetUnsupportedReason
        ? t(`presetUnsupported.${presetUnsupportedReason}` as Parameters<typeof t>[0])
        : null,
    [presetUnsupportedReason, t]
  )

  const handleOpen = useCallback(
    (nextOpen: boolean) => {
      if (nextOpen) {
        if (unsupportedMessage) {
          toast.error(unsupportedMessage, { position: 'bottom-right' })
          return
        }
        // Every leg is saved with its row's account, so a row without a valid one
        // can't be saved (this toasts which rows).
        if (!validateSubmissionRows()) return
        const payload = buildCurrentPresetPayload()
        if (!payload) {
          toast.error(t('selectInstrumentFirst'), { position: 'bottom-right' })
          return
        }
        // The dialog opening is the tour's finish line — the rest (naming,
        // saving) is the same flow every other preset save goes through.
        if (tour?.step === 'save') {
          setTour(null)
          setTourDone(true)
        }
      }
      setOpen(nextOpen)
      if (nextOpen) {
        setName('')
        setTimeout(() => inputRef.current?.focus(), 50)
      }
    },
    [
      buildCurrentPresetPayload,
      validateSubmissionRows,
      unsupportedMessage,
      t,
      tour,
      setTour,
      setTourDone,
    ]
  )

  const handleSave = useCallback(async () => {
    const trimmed = name.trim()
    if (!trimmed) return

    const payload = buildCurrentPresetPayload()
    if (!payload) {
      toast.error(t('selectInstrumentFirst'), { position: 'bottom-right' })
      return
    }

    try {
      const preset = await createPreset({ name: trimmed, payload })
      const resolvedName = preset?.name ?? trimmed
      toast.success(
        resolvedName !== trimmed
          ? t('presetSavedAs', { name: resolvedName })
          : t('presetSaved', { name: resolvedName })
      )
      setOpen(false)
      setName('')
    } catch {
      toast.error(t('failedSavePreset'), { position: 'bottom-right' })
    }
  }, [name, buildCurrentPresetPayload, createPreset, t])

  return (
    <Dialog open={open} onOpenChange={handleOpen}>
      {/* aria-disabled instead of the native `disabled` attribute so the native
          hover title can still explain why preset saving is unavailable. */}
      <Button
        type="button"
        variant="ghost"
        size="sm"
        aria-disabled={!!unsupportedMessage}
        aria-label={unsupportedMessage ?? t('saveAsPreset')}
        title={unsupportedMessage ?? t('saveAsPreset')}
        data-preset-tour-target={PRESET_TOUR_TARGET.saveStar}
        data-tutorial="shortcut"
        onClick={() => handleOpen(true)}
        className={cn(
          'text-foreground/40 hover:text-foreground/60 hover:bg-foreground/5 relative h-fit shrink-0 p-1 ring-0 outline-none hover:bg-white/5 [&_svg]:outline-none',
          unsupportedMessage && 'hover:text-foreground/40 cursor-not-allowed opacity-40'
        )}
      >
        <Star size={14} />
      </Button>
      <DialogContent className="min-h-0 w-full max-w-md gap-4 p-6">
        <DialogHeader className="space-y-1">
          <DialogTitle>{t('saveAsPreset')}</DialogTitle>
          <p className="text-foreground/50 text-xs leading-4">{t('savePresetDescription')}</p>
        </DialogHeader>

        {open && <OrderDraftSummary t={t} />}

        <Input
          ref={inputRef}
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleSave()
            if (e.key === 'Escape') setOpen(false)
          }}
          placeholder={t('presetNamePlaceholder')}
          inputClassName="h-9 px-2 outline-none"
        />
        <div className="flex items-center justify-end gap-2">
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="text-foreground/50 hover:text-foreground h-8 px-3 hover:bg-white/10"
            onClick={() => setOpen(false)}
          >
            {t('cancel')}
          </Button>
          <Button
            type="button"
            size="sm"
            className="bg-emphasis hover:bg-emphasis/90 text-emphasis-foreground h-8 min-w-18 px-3"
            disabled={!name.trim() || isPending}
            onClick={handleSave}
          >
            {isPending ? <Loader2 className="size-3.5 animate-spin" /> : t('save')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
